import React, { useState, memo, useContext, useMemo } from 'react'
import styled, { ThemeContext } from 'styled-components'
import StatsCard from './StatsCard'
import {
  Cell,
  PieChart,
  Pie,
  ResponsiveContainer,
  Tooltip,
  LabelList
} from 'recharts'
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap'
import { capitalize } from 'utils'
import numeral from 'numeral'
import MotivationalMessage from './MotivationalMessage'
import { PieLabelArray } from './PieLabelArray'
import {
  removeSmallData,
  renderCustomPieChartLabel,
  renderCustomPieChartLabelLine
} from '../util'
import {
  renderWhitePiePercentLabel,
  renderWhitePieLabelLine
} from '../util/pieChartLabel'

const DropdownContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
`

const containerStyles = `
  grid-row-start: 3;
  grid-row-end: span 2;
`

const locationTypes = ['cities', 'states', 'countries']

const formatTooltipLabel = (value, name, props) => [
  numeral(value).format('$0,0[.]00'),
  name
]

// Custom label function for Recharts 2.x
const PiePercentLabel = ({ percent, x, y, index, ...rest }) => {
  // Only show for slices >= 3%
  if (percent < 0.03) return null
  return (
    <text
      x={x}
      y={y}
      fontSize={14}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
    >
      {`${Math.round(percent * 100)}%`}
    </text>
  )
}

export const SalesByLocation = memo(({ stats, merchantCity, tooltipText }) => {
  const [locationType, setLocationType] = useState('cities')
  const theme = useContext(ThemeContext)
  const bluePalette = theme.dashboard.chartSalesGradient || []

  const sortedData = useMemo(() => {
    let processedData = []
    if (stats && stats[locationType]) {
      processedData = Object.keys(stats[locationType]).map(key => {
        const value = stats[locationType][key] || 0
        return { name: key, value }
      })
      processedData = [...processedData].sort((a, b) => b.value - a.value)
    }
    return processedData
  }, [stats, locationType])

  return (
    <StatsCard
      title={'Sales by Location'}
      containerStyles={containerStyles}
      tooltipText={tooltipText}
    >
      {sortedData?.length && sortedData.some(item => item.value > 0) ? (
        <>
          <DropdownContainer>
            <UncontrolledDropdown>
              <DropdownToggle
                style={{ fontSize: '0.875rem' }}
                color="primary"
                caret
              >
                {capitalize(locationType)}
              </DropdownToggle>
              <DropdownMenu right={true}>
                {locationTypes.map((type, index) => (
                  <DropdownItem
                    style={{ fontSize: '0.875rem' }}
                    key={`location-type-${index}`}
                    onClick={() => setLocationType(type)}
                  >
                    {capitalize(type)}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </UncontrolledDropdown>
          </DropdownContainer>
          <div style={{ width: '100%' }}>
            <ResponsiveContainer height={200} width="100%">
              <PieChart>
                <Pie
                  dataKey="value"
                  data={sortedData}
                  cx="50%"
                  cy="50%"
                  innerRadius={5}
                  outerRadius={50}
                  label={renderWhitePiePercentLabel}
                  labelLine={renderWhitePieLabelLine}
                >
                  {sortedData.map((entry, index) => {
                    const colorIndex = index % bluePalette.length
                    const fillColor = bluePalette[colorIndex]
                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={fillColor}
                        stroke={fillColor}
                        strokeWidth={1}
                      />
                    )
                  })}
                </Pie>
                <Tooltip formatter={formatTooltipLabel} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <PieLabelArray
            data={sortedData}
            shortenTo={3}
            elementId={'sales-by-location-labels'}
            colorPalette={bluePalette}
          />
        </>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexGrow: 1
          }}
        >
          <MotivationalMessage message="No sales data found for this location type." />
        </div>
      )}
    </StatsCard>
  )
})
